// src/services/getPresignedUrlREST.ts
import axios from 'axios';
import { API_GATEWAY } from '../constants/api-gateway.constants';
import { ErrorMessages } from '../constants/error-messages';
import dataURItoBlob from '../helpers/dataURItoBlob';
import { validateImageFile } from '../helpers/validateImageFile';

/**
 * Error messages
 */
export const UPLOAD_ERRORS = {
  ...ErrorMessages.PRE_SIGNED_URL_ERRORS,
  FILE_TOO_LARGE: 'Image exceeds the maximum size of 3MB',
  INVALID_FILE_TYPE:
    'Invalid file type. Only JPEG, PNG, GIF, and WEBP are allowed',
  UPLOAD_FAILED: 'Failed to upload image',
};

/**
 * Normalises an avatar key that may have been stored as a full S3 URL.
 * Older records saved the full URL instead of just the object key.
 */
const normalizeAvatarKey = (key: string): string => {
  if (!key.startsWith('http')) return key;
  try {
    const { pathname } = new URL(key);
    return pathname.startsWith('/') ? pathname.slice(1) : pathname;
  } catch {
    return key;
  }
};

/**
 * Builds a direct S3 URL for an avatar key.
 * Requires the bucket to allow public reads (or use a CloudFront distribution).
 */
export const buildAvatarUrl = (key: string): string => {
  const normalizedKey = normalizeAvatarKey(key);
  const stage = import.meta.env.VITE_STAGE || 'dev';
  const region = import.meta.env.VITE_AWS_REGION || 'us-east-2';
  const bucket = `users-profile-avatar-${stage}`;
  return `https://${bucket}.s3.${region}.amazonaws.com/${normalizedKey}`;
};

/**
 * Gets a presigned URL for uploading an image to S3 via REST API
 * @param fileType The MIME type of the file
 * @param fileSize The size of the file in bytes
 * @returns A promise that resolves to an object containing the presigned URL, key, and imageUrl
 */
export const getPresignedUrl = async (
  _: any,
  payload: any,
): Promise<{ presignedUrl: string; key: string; imageUrl: string | null }> => {
  const { fileType, fileSize } = payload.input;

  try {
    const apiBaseUrl =
      import.meta.env.VITE_API_GET_USER_AVATAR ||
      'https://rz8o5emw06.execute-api.us-east-2.amazonaws.com';
    const stage = import.meta.env.VITE_STAGE || 'dev';

    const url = `${apiBaseUrl}/${stage}/presigned-url`;

    const response = await axios.post(url, {
      fileType,
      fileSize,
    });

    // Validate the response contains the expected fields
    if (!response.data || !response.data.presignedUrl || !response.data.key) {
      console.error('Invalid response from presigned URL API:', response.data);
      throw new Error('Server returned an invalid response format');
    }

    // Validate the presigned URL contains proper credentials
    if (!response.data.presignedUrl.includes('X-Amz-Credential=')) {
      console.error(
        'Invalid presigned URL format (missing credentials):',
        response.data.presignedUrl,
      );
      throw new Error('Server generated an invalid presigned URL');
    }

    // Avatar Lambda returns only presignedUrl and key; imageUrl is obtained separately via getAvatarUrl
    const imageUrl = response.data.imageUrl || null;

    return {
      presignedUrl: response.data.presignedUrl,
      key: response.data.key,
      imageUrl,
    };
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    if (axios.isAxiosError(error)) {
      console.error('API error details:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    throw new Error(UPLOAD_ERRORS.FAILED_TO_GET_PRESIGNED_URL);
  }
};

/**
 * Uploads a file to S3 using a presigned URL
 * @param presignedUrl The presigned URL for the upload
 * @param file The file to upload
 * @param contentType The content type of the file
 * @returns A promise that resolves to the upload status
 */
export const uploadToS3 = async (
  presignedUrl: string,
  file: File | Blob,
  contentType: string,
): Promise<number> => {
  try {
    // Use axios to upload the file directly to S3
    const response = await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType,
        'Cache-Control': 'max-age=31536000',
      },
      timeout: 30000,
    });

    return response.status;
  } catch (error) {
    console.error('Error uploading to S3:', error);

    if (axios.isAxiosError(error)) {
      console.error('S3 upload error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });

      // Extract error message from XML response
      if (typeof error.response?.data === 'string') {
        const errorMatch = error.response.data.match(
          /<Message>(.*?)<\/Message>/,
        );
        if (errorMatch && errorMatch[1]) {
          console.error('S3 Error Message:', errorMatch[1]);
        } else {
          console.error(
            'S3 Error Response:',
            error.response.data.substring(0, 500),
          );
        }
      }
    }

    throw new Error(UPLOAD_ERRORS.FAILED_TO_UPLOAD);
  }
};

/**
 * Fetches avatar image data from S3 via the Lambda proxy and returns a data URL.
 * Data URLs are treated as same-origin by the canvas API, so drawing them
 * onto a canvas does not taint it — toDataURL() works without S3 CORS config.
 * @param key The S3 object key
 * @returns A promise that resolves to a data: URL for the image
 */
export const getAvatarImageDataUrl = async (key: string): Promise<string> => {
  const normalizedKey = normalizeAvatarKey(key);
  const apiBaseUrl =
    import.meta.env.VITE_API_GET_USER_AVATAR ||
    'https://rz8o5emw06.execute-api.us-east-2.amazonaws.com';
  const stage = import.meta.env.VITE_STAGE || 'dev';
  const url = `${apiBaseUrl}/${stage}/${API_GATEWAY.GET_AVATAR_IMAGE}/${normalizedKey}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch avatar image data');
  const { dataUrl } = await response.json();
  if (!dataUrl) throw new Error('Invalid response: missing dataUrl');
  return dataUrl;
};

/**
 * Gets the avatar URL from a key
 * @param key The S3 object key (or full S3 URL for legacy records)
 * @returns A promise that resolves to the avatar URL
 */
export const getAvatarUrl = async (key: string): Promise<string> => {
  const normalizedKey = normalizeAvatarKey(key);
  try {
    const apiBaseUrl =
      import.meta.env.VITE_API_GET_USER_AVATAR ||
      'https://rz8o5emw06.execute-api.us-east-2.amazonaws.com';
    const stage = import.meta.env.VITE_STAGE || 'dev';
    const url = `${apiBaseUrl}/${stage}/${API_GATEWAY.GET_AVATAR}/${normalizedKey}`;

    const response = await axios.get(url);
    return response.data.presignedUrl;
  } catch (error) {
    console.error('Error getting avatar URL:', error);
    throw new Error('Failed to get avatar URL');
  }
};

/**
 * Updates a user's avatar in their preferences
 * @param userId The user's ID
 * @param key The S3 object key of the avatar
 * @param updateUserPreferences The mutation function for updating user preferences
 * @returns A promise that resolves when the update is complete
 */
export const updateUserAvatar = async (
  userId: string,
  key: string,
  updateUserPreferences: (variables: any) => Promise<any>,
): Promise<void> => {
  try {
    const payload = {
      input: {
        avatar: key,
        userId,
      },
    };

    await updateUserPreferences({ variables: payload });
  } catch (error) {
    console.error('Error updating user avatar:', error);
    throw new Error('Failed to update user preferences');
  }
};

/**
 * Uploads an image file as a user's avatar
 * @param file The file to upload
 * @param userId The user's ID
 * @param updateUserPreferences The mutation function for updating user preferences
 * @returns A promise that resolves to an object containing the upload status, key, and URL
 */
export const uploadAvatar = async (
  file: File,
  userId: string,
  updateUserPreferences: (variables: any) => Promise<any>,
): Promise<{ status: number; key: string; url: string }> => {
  // Validate the file
  const validation = validateImageFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error || 'Invalid file');
  }

  try {
    // Get presigned URL
    const payload = {
      input: {
        fileType: file.type,
        fileSize: file.size,
      },
    };

    const { presignedUrl, key } = await getPresignedUrl(null, payload);

    // Upload to S3
    const status = await uploadToS3(presignedUrl, file, file.type);

    if (status === 200) {
      // Update user preferences with new avatar
      await updateUserAvatar(userId, key, updateUserPreferences);

      let url = buildAvatarUrl(key);
      try {
        url = await getAvatarUrl(key);
      } catch {
        // fallback to direct S3 URL; AvatarComponent handles 403 with onError
      }
      return { status, key, url };
    } else {
      throw new Error(UPLOAD_ERRORS.FAILED_TO_UPLOAD);
    }
  } catch (error) {
    console.error('Error in uploadAvatar:', error);
    throw error instanceof Error
      ? error
      : new Error(UPLOAD_ERRORS.UPLOAD_FAILED);
  }
};

/**
 * Uploads an edited avatar image to S3
 * @param editedImageData The edited image data (as a data URI)
 * @param userId The user's ID
 * @param updateUserPreferences The mutation function for updating user preferences
 * @returns A promise that resolves to an object containing the key and URL
 */
export const uploadEditedAvatar = async (
  editedImageData: string,
  userId: string,
  updateUserPreferences: (variables: any) => Promise<any>,
): Promise<{ key: string; url: string }> => {
  try {
    // Get a presigned URL for uploading
    const blob = dataURItoBlob(editedImageData);
    const payload = {
      input: {
        fileType: 'image/png',
        fileSize: blob.size,
      },
    };

    const { presignedUrl, key } = await getPresignedUrl(null, payload);

    // Upload the edited image
    await uploadToS3(presignedUrl, blob, 'image/png');

    // Update user preferences with new avatar key
    await updateUserAvatar(userId, key, updateUserPreferences);

    let url = buildAvatarUrl(key);
    try {
      url = await getAvatarUrl(key);
    } catch {
      // fallback to direct S3 URL; AvatarComponent handles 403 with onError
    }
    return { key, url };
  } catch (error) {
    console.error('Error uploading edited avatar:', error);
    throw new Error(ErrorMessages.AVATAR.UPDATE_FAILED);
  }
};
